import { IContinent } from '../../../../shared/components/src/lib/location-selection/location.interface';
import {
  WeightedWidgetPanelState,
  WidgetPanelState,
  IRangeSelectorOption,
} from '../interfaces';
import { WidgetTypes } from './widget-types';
import { DEFAULT_WIDGET_DIFFERENCES_SELECTION } from '../../../../weighted/src/lib/components/model-forecast-differences-selection/model-forecast-differences-selection.constants';
import {
  CategoryValues,
  ContinentNames,
  DailyPeriodValues,
  ForecastValues,
  RunTimeModelValues,
} from '../enums';
import { ContinentId } from '@firebird-web/shared-constants';

export const TempUnitC = 'C';
export const ListOfContinents: IContinent[] = [
  { name: 'North America', id: 'NA' },
  { name: 'Europe', id: 'EUR' },
  { name: 'Asia', id: 'ASIA' },
  { name: 'Mexico', id: 'MEX' },
];
export const DeFaultWidgetPanelState: WidgetPanelState = {
  selectedContinent: '',
  selectedRegion: '',
  selectedCity: '',
  selectedSiteId: '',
  selectedSecondaryLens: '',
  diffPriorValue: false,
  showDecimalValue: false,
  selectedSumTableSize: 'full',
  displayName: '',
};

export const DefaultWeightedWidgetPanelState: WeightedWidgetPanelState = {
  selectedCategory: CategoryValues.iso,
  selectedContinent: '',
  selectedRegionId: '',
  selectedRegionName: '',
  selectedForecast: ForecastValues.ag2,
  selectedClimatology: 'AvgYrThirty',
  selectedTableSize: 'full',
  selectedRunTimeModel: RunTimeModelValues.runTime,
  selectedRunTimeData: '',
  selectedParameterValue: '',
  selectedDailyPeriod: DailyPeriodValues.daily,
  diffPriorValue: false,
  differencesSelection: DEFAULT_WIDGET_DIFFERENCES_SELECTION,
  showDecimalValue: false,
  normalValue: false,
  biasCorrected: false,
};

export const WidgetLabelConfig: Record<string, string> = {
  [WidgetTypes.heatMap]: 'Summary Table',
  [WidgetTypes.renewables]: 'Renewables',
  [WidgetTypes.analysis]: 'Forecast Analysis',
  [WidgetTypes.weighted]: 'Weighted Forecast Table',
  [WidgetTypes.model]: 'Model Data',
  // [WidgetTypes.weightedGraph]: 'Weighted Forecast Graph',
  [WidgetTypes.weightedGraphDifferences]: 'Weighted Forecast Graph',
  [WidgetTypes.demand]: 'Demand',
  [WidgetTypes.periodMapsHeadlines]: 'Period Maps and Headlines',
  [WidgetTypes.ec46WeightedDDGraph]: 'EC 46 Weighted Graph',
  [WidgetTypes.teleconnection]: 'Teleconnections',
};

export const WeightedContinentList = [
  { text: ContinentNames.na, id: 'NA' },
  { text: ContinentNames.eur, id: 'EUR' },
  { text: ContinentNames.asia, id: 'ASIA' },
];

export const DefaultLocation: Record<string, { [key: string]: string }> = {
  [CategoryValues.iso]: {
    [ContinentNames.na]: 'MISO',
    [ContinentNames.eur]: 'ITALY',
    [ContinentNames.asia]: 'CHINA',
  },
  [CategoryValues.degree]: {
    [ContinentNames.na]: 'MOUNTAIN',
    [ContinentNames.eur]: 'NORWAY',
    [ContinentNames.asia]: 'CHINA',
  },
};

export const RANGE_SELECTOR_OPTIONS: Array<IRangeSelectorOption> = [
  {
    label: 'Previous 24 Hrs',
    value: {
      min: -1,
      max: 0,
    },
  },
  {
    label: '24 Hrs',
    value: {
      min: 0,
      max: 1,
    },
  },
  {
    label: '48 Hrs',
    value: {
      min: 0,
      max: 2,
    },
  },
  {
    label: '1-7 Days',
    value: {
      min: 0,
      max: 7,
    },
  },
  {
    label: '1-15 Days',
    value: {
      min: 0,
      max: 15,
    },
  },
  {
    label: 'Full Range',
    value: {
      min: -6,
      max: 15,
    },
  },
];

export const WEIGHTED_CONTINENT_MAP: Record<string, string> = {
  NA: 'North America',
  EUR: 'Europe',
  ASIA: 'Asia',
  MEX: 'Mexico',
};

export const DEFAULT_EC46_WEIGHTED_GRAPH_LEGEND_STATE = {
  modelRuns: [false, false, false, false, false],
  normals: [false, false, false],
  obs: false,
};

export const EC46_GRAPH_MODEL_RUN_OPTIONS = [
  { labelKey: 'Latest' },
  { labelKey: 'Latest-1' },
  { labelKey: 'Latest-2' },
  { labelKey: 'Latest-3' },
  { labelKey: 'Latest-4' },
];

export const EC46_GRAPH_NORMAL_OPTIONS = [
  { labelKey: 'thirtyYearAverage' },
  { labelKey: 'tenYearAverage' },
  { labelKey: 'priorOneYearAverage' },
];

export type ContinentFromUrlInfo = {
  isUrlLocked: boolean;
  continentFromUrl: ContinentId | null;
};

export const WEIGHTED_URL_SEGMENT = 'region-view/weighted';
export const RENEWABLES_URL_SEGMENT = 'region-view/renewables';
const DEMAND_URL_SEGMENT = 'region-view/demand';
interface UrlLockedContinentResolver {
  matches: (url: string) => boolean;
  resolveContinent: (url: string) => ContinentId | null;
}

const queryParamContinentResolver = (
  urlSegment: string,
  paramName: string
): UrlLockedContinentResolver => ({
  matches: (url) => url.includes(urlSegment),
  resolveContinent: (url) =>
    new URLSearchParams(url.split('?')[1] ?? '').get(
      paramName
    ) as ContinentId | null,
});

/**
 * Pages that resolve their own continent from the url instead of the shared
 * location-picker store.
 */
const URL_LOCKED_CONTINENT_RESOLVERS: UrlLockedContinentResolver[] = [
  queryParamContinentResolver(WEIGHTED_URL_SEGMENT, 'weightedContinent'),
  {
    matches: (url) => url.includes(RENEWABLES_URL_SEGMENT),
    resolveContinent: (url) =>
      (url.match(/region-view\/renewables\/([^/?#]+)/)?.[1] ??
        null) as ContinentId | null,
  },
  {
    // demand is North America only and has no continent selector of its own
    matches: (url) => url.includes(DEMAND_URL_SEGMENT),
    resolveContinent: () => 'NA' as ContinentId,
  },
];

const getContinentFromUrl = (url: string): ContinentId | null => {
  const resolver = URL_LOCKED_CONTINENT_RESOLVERS.find((r) => r.matches(url));
  return resolver?.resolveContinent(url) ?? null;
};

const getIsValidCommentaryContinent = (
  value: string | null,
  continents: Record<string, string>
): value is ContinentId => {
  if (!value) return false;
  return (Object.values(continents) as string[]).includes(value);
};

export const resolveValidContinentFromUrl = (
  url: string,
  continents: Record<string, string>
): ContinentFromUrlInfo => {
  const continentFromUrl = getContinentFromUrl(url);
  const isUrlLocked = getIsValidCommentaryContinent(
    continentFromUrl,
    continents
  );
  return isUrlLocked
    ? { isUrlLocked, continentFromUrl }
    : { isUrlLocked: false, continentFromUrl: null };
};

export function normalizeRegionName(region?: string | null): string | null {
  if (!region) return null;
  return region
    .replace(/-FIRE$/, '')
    .replace(/_/g, ' ')
    .trim();
}
